// src/utils/vofStatementXlsx.js
// ============================================================================
// XLSX export of the VOF hours statement: one sheet per section (Summary,
// Module A per project, Detail, Excluded), built from buildStatement() so it
// prints exactly what the page shows. Notes are written in full, including
// "Reconstructed estimate" rows.
// ============================================================================

import ExcelJS from 'exceljs';
import { getExcelStyle } from './excelStyles';
import { DETAIL_COLUMNS, EXCLUDED_COLUMNS, EXCLUDED_BUCKETS, formatDay, statementFileName } from './vofStatement';

const BRIO_GOLD = 'FF9F902A';
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRIO_GOLD } };
const TOTAL_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F0D0' } };

const headerFont = () => ({ ...getExcelStyle('style1'), bold: true, color: { argb: 'FFFFFFFF' } });
const bodyFont = () => ({ ...getExcelStyle('style1') });

const addTable = (ws, header, rows, { numberCols = [], moneyCols = [], widths = [] } = {}) => {
  const head = ws.addRow(header);
  head.eachCell(c => { c.font = headerFont(); c.fill = HEADER_FILL; });
  rows.forEach(r => {
    const row = ws.addRow(r);
    row.eachCell(c => { c.font = bodyFont(); });
  });
  numberCols.forEach(i => { ws.getColumn(i).numFmt = '#,##0.00'; });
  moneyCols.forEach(i => { ws.getColumn(i).numFmt = '#,##0.00'; });
  widths.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
  ws.views = [{ state: 'frozen', ySplit: ws.rowCount - rows.length }];
};

const titleRows = (ws, title, period) => {
  const t = ws.addRow([title]);
  t.getCell(1).font = { ...getExcelStyle('style2'), bold: true };
  ws.addRow([`Period: ${formatDay(period.from)} to ${formatDay(period.to)}`]).getCell(1).font = bodyFont();
  ws.addRow([]);
};

export async function exportVofStatementXlsx(statement, period) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'BRIO Atlas';
  wb.created = new Date();
  const cur = statement.currency;

  // Summary
  const ws = wb.addWorksheet('Summary');
  titleRows(ws, 'VOF hours statement: Architecture BRIO V.O.F. to Studio BRIO Pvt Ltd', period);
  const summary = statement.partners.map(p => [
    p.userName, p.moduleA.hours, p.moduleA.amount, p.moduleB.hours, p.moduleB.amount, p.total.hours, p.total.amount,
    ...EXCLUDED_BUCKETS.map(b => p.excluded[b] || 0),
  ]);
  addTable(ws,
    ['Partner', 'Module A h', `Module A ${cur}`, 'Module B h', `Module B ${cur}`, 'Total h', `Total ${cur}`, ...EXCLUDED_BUCKETS.map(b => `Excluded: ${b} (h)`)],
    summary,
    { numberCols: [2, 3, 4, 5, 6, 7, 8, 9, 10, 11], widths: [22, 12, 14, 12, 14, 12, 14, 18, 18, 18, 18] });
  const total = ws.addRow(['Total', statement.totals.moduleAHours, statement.totals.moduleAAmount, statement.totals.moduleBHours, statement.totals.moduleBAmount, statement.totals.hours, statement.totals.amount]);
  total.eachCell(c => { c.font = { ...bodyFont(), bold: true }; c.fill = TOTAL_FILL; });

  // Module A per project
  const wp = wb.addWorksheet('Module A per project');
  titleRows(wp, 'Module A: partner hours on SBPL projects, worked from the Netherlands', period);
  addTable(wp, ['Partner', 'Project code', 'Project', 'Hours', `Amount ${cur}`],
    statement.projectsA.map(p => [p.userName, p.projectCode, p.projectName, p.hours, p.amount]),
    { numberCols: [4, 5], widths: [22, 12, 32, 10, 14] });

  // Detail
  const wd = wb.addWorksheet('Detail');
  titleRows(wd, 'Detail: every Module A and Module B entry', period);
  addTable(wd, DETAIL_COLUMNS.map(c => c[0]),
    statement.detail.map(r => DETAIL_COLUMNS.map(c => c[1](r))),
    { numberCols: [7, 8, 10], widths: [12, 20, 12, 10, 28, 22, 8, 8, 8, 12, 40, 40] });
  wd.getColumn(11).alignment = { wrapText: true, vertical: 'top' };
  wd.getColumn(12).alignment = { wrapText: true, vertical: 'top' };

  // Excluded
  const we = wb.addWorksheet('Excluded');
  titleRows(we, 'Excluded partner entries: not invoiced by ABVOF', period);
  addTable(we, EXCLUDED_COLUMNS.map(c => c[0]),
    statement.excludedRows.map(r => EXCLUDED_COLUMNS.map(c => c[1](r))),
    { numberCols: [9], widths: [12, 20, 34, 30, 18, 10, 28, 22, 8, 40, 40] });

  const buffer = await wb.xlsx.writeBuffer();
  return { buffer, fileName: statementFileName(period, 'xlsx') };
}
